// The branch's content catalog (spec/dashboard.md sections 5–6): templates, enums, localization strings, assets, products
// and tags, loaded once per branch head and shared by the grid, the editors and the pickers; the document resolver for
// labels; one FormatContext for every formatter.
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { ops } from '../api/operations';
import type { Asset, EnumDefinition, LocalizationSettings, LocalizationString, Product, Tag, Template } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useGame } from '../shell/GameContext';
import { useTimeZone } from '../ui/time';
import { useQuery } from '../workspace/cache';
import { useOptionalWorkspace, useWorkspaceVersion } from '../workspace/react';
import { entityKey } from '../workspace/Workspace';
import { labelText, type DocumentLike, type FormatContext } from './format';
import { useDocumentResolver, type DocumentResolver } from './resolver';

export interface ContentCatalog {
  branch: string;
  templates: Map<string, Template>;
  templateList: Template[];
  enums: Map<string, EnumDefinition>;
  /** by id */
  strings: Map<string, LocalizationString>;
  stringList: LocalizationString[];
  groups: string[];
  assets: Map<string, Asset>;
  assetList: Asset[];
  /** by id and by itemId */
  products: Map<string, Product>;
  productList: Product[];
  tags: Tag[];
  languages: { code: string; name: string }[];
  defaultLanguage: string;
  loading: boolean;
  error: string | null;
  resolve: (id: string) => DocumentLike | undefined;
  resolver: DocumentResolver;
  /** the context every formatter takes (labels, cells, cards) */
  format: FormatContext;
  /** the label of a document by its template's display format */
  label: (doc: DocumentLike) => string;
  /** the default-language text of a string (a staged new string included) */
  stringText: (id: string) => string | undefined;
  /** re-read the catalogs (after an upload, a schema change) */
  refresh: () => void;
  /** moves when any catalog or the resolver changes */
  version: number;
}

/** Exported for the catalogs built from something other than a branch (the players pages' release schema, section 11.2). */
export const ContentContext = createContext<ContentCatalog | null>(null);

export function useContent(): ContentCatalog {
  const value = useContext(ContentContext);
  if (!value) throw new Error('useContent outside a ContentProvider');
  return value;
}

export function useOptionalContent(): ContentCatalog | null {
  return useContext(ContentContext);
}

export function ContentProvider({ children }: { children: ReactNode }) {
  const { org, game, offsets } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const workspace = useOptionalWorkspace();
  const workspaceVersion = useWorkspaceVersion(workspace ?? undefined);
  const [zone] = useTimeZone();
  const head = workspace?.head ?? null;
  const path = { org, game: game.slug, branch };

  const templatesQ = useQuery(`branch:${game.id}:${branch}:templates:all`, () => client.all<Template>((cursor) => client.data(ops.listTemplates, { path, query: { limit: 200, cursor } })), { head });
  const enumsQ = useQuery(`branch:${game.id}:${branch}:enums`, () => client.all<EnumDefinition>((cursor) => client.data(ops.listEnums, { path, query: { limit: 200, cursor } })), { head });
  const stringsQ = useQuery(`branch:${game.id}:${branch}:strings:all`, () => client.all<LocalizationString>((cursor) => client.data(ops.listLocalizationStrings, { path, query: { limit: 200, cursor } })), { head });
  const settingsQ = useQuery(`branch:${game.id}:${branch}:localization-settings`, () => client.data(ops.getLocalizationSettings, { path }), { head });
  const assetsQ = useQuery(`branch:${game.id}:${branch}:assets:all`, () => client.all<Asset>((cursor) => client.data(ops.listAssets, { path, query: { limit: 200, cursor } })), { head });
  const productsQ = useQuery(`branch:${game.id}:${branch}:products:all`, () => client.all<Product>((cursor) => client.data(ops.listProducts, { path, query: { limit: 200, cursor } })), { head });
  const groupsQ = useQuery(`branch:${game.id}:${branch}:localization-groups`, () => client.data(ops.listLocalizationGroups, { path }), { head });
  const tagsQ = useQuery(`branch:${game.id}:${branch}:tags`, () => client.data(ops.listTags, { path }).then((r) => r.items), { head });
  const { resolve, resolver, version: resolverVersion } = useDocumentResolver(client, org, game.slug, branch, head, workspace);

  const templates = useMemo(() => new Map((templatesQ.data ?? []).map((t) => [t.name, t])), [templatesQ.data]);
  const enums = useMemo(() => new Map((enumsQ.data ?? []).map((e) => [e.name, e])), [enumsQ.data]);
  const strings = useMemo(() => new Map((stringsQ.data ?? []).map((s) => [s.id, s])), [stringsQ.data]);
  const assets = useMemo(() => new Map((assetsQ.data ?? []).filter((a) => !a.deletedAt).map((a) => [a.id, a])), [assetsQ.data]);
  const products = useMemo(() => {
    const m = new Map<string, Product>();
    for (const p of productsQ.data ?? []) {
      m.set(p.id, p);
      if (p.itemId) m.set(p.itemId, p);
    }
    return m;
  }, [productsQ.data]);
  const settings = settingsQ.data as LocalizationSettings | undefined;
  const languages = useMemo(() => (settings?.languages ?? [{ code: 'en', name: 'English', enabled: true, order: 1 }]).filter((l) => l.enabled !== false).sort((a, b) => Number(a.order) - Number(b.order)).map((l) => ({ code: l.code, name: l.name ?? l.code })), [settings]);
  const defaultLanguage = settings?.defaultLanguage ?? 'en';
  const groups = useMemo(() => Array.from(new Set([...((groupsQ.data as { name: string }[] | undefined) ?? []).map((g) => g.name), ...(stringsQ.data ?? []).map((s) => s.group)])).sort(), [groupsQ.data, stringsQ.data]);

  const stringText = useCallback(
    (id: string): string | undefined => {
      const draft = workspace?.getDraft(entityKey('string', id));
      const s = (draft?.current as unknown as LocalizationString | undefined) ?? strings.get(id);
      if (!s) return undefined;
      const values = (s.values ?? {}) as Record<string, string>;
      return values[defaultLanguage] ?? Object.values(values)[0] ?? s.key;
    },
    [strings, workspace, defaultLanguage, workspaceVersion], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const format = useMemo<FormatContext>(
    () => ({
      enums,
      templates,
      zone,
      offsets,
      resolve,
      stringText,
      assetName: (id) => assets.get(id)?.name,
      productLabel: (productId) => {
        const p = products.get(productId);
        return p ? productLabelOf(p) : undefined;
      },
    }),
    [enums, templates, zone, offsets, resolve, stringText, assets, products],
  );

  const label = useCallback((doc: DocumentLike) => labelText(doc, format), [format]);
  const refresh = useCallback(() => {
    templatesQ.refetch();
    enumsQ.refetch();
    stringsQ.refetch();
    settingsQ.refetch();
    assetsQ.refetch();
    productsQ.refetch();
    tagsQ.refetch();
    groupsQ.refetch();
    resolver.invalidate();
  }, [templatesQ, enumsQ, stringsQ, settingsQ, assetsQ, productsQ, tagsQ, groupsQ, resolver]);

  const loading = templatesQ.loading || enumsQ.loading || stringsQ.loading || assetsQ.loading || productsQ.loading;
  const firstError = [templatesQ.error, enumsQ.error, stringsQ.error, assetsQ.error, productsQ.error, tagsQ.error].find((e) => e);
  const value = useMemo<ContentCatalog>(
    () => ({
      branch,
      templates,
      templateList: templatesQ.data ?? [],
      enums,
      strings,
      stringList: stringsQ.data ?? [],
      groups,
      assets,
      assetList: Array.from(assets.values()),
      products,
      productList: productsQ.data ?? [],
      tags: (tagsQ.data ?? []) as Tag[],
      languages,
      defaultLanguage,
      loading,
      error: firstError ? String(firstError instanceof Error ? firstError.message : firstError) : null,
      resolve,
      resolver,
      format,
      label,
      stringText,
      refresh,
      version: resolverVersion + workspaceVersion,
    }),
    [branch, templates, templatesQ.data, enums, strings, stringsQ.data, groups, assets, products, productsQ.data, tagsQ.data, languages, defaultLanguage, loading, firstError, resolve, resolver, format, label, stringText, refresh, resolverVersion, workspaceVersion],
  );
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

/** `Coins · 3999 NG` (the amount is in minor units, the currency a registry code). */
export function productLabelOf(p: Product): string {
  const price = p.price as unknown as { amount?: number | string; currency?: string } | null;
  const amount = price?.amount ?? '?';
  const currency = price?.currency ?? '';
  return `${p.name} · ${amount} ${currency}`.trim();
}
